/**
 * Il riquadro di un widget: colore, titolo, "Apri", controlli di modifica e
 * protezione dai guasti. Il contenuto lo disegna il widget.
 *
 * Fuori dalla modifica il widget e' un oggetto pieno di colore, come sul
 * telefono (i colori stanno in temi.ts). In modifica "traballa" e i controlli
 * stanno sui bordi: il pallino "−" per toglierlo, "⋯" per il menu, le formine
 * in basso per la taglia. Si trascina prendendolo in qualunque punto.
 *
 * Il contenuto in modifica non si puo' cliccare (un collegamento non deve
 * portare via a meta' lavoro); i controlli restano raggiungibili anche se il
 * widget si e' guastato: un widget rotto si puo' sempre togliere.
 */

import { memo, useEffect, useRef, type ComponentType, type CSSProperties, type HTMLAttributes, type SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Minus, MoreHorizontal, Settings2 } from 'lucide-react';
import { cn } from '../../lib/cn';
import { CATALOGO, type Taglia } from '../../lib/dashboard/catalogo';
import type { AzioneLayout, VersoSpostamento, WidgetIstanza } from '../../lib/dashboard/layout';
import { ErrorBoundary } from '../ErrorBoundary';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { CLASSI_TAGLIA, ETICHETTE_TAGLIA } from './taglie';
import { ContestoTema } from './temaContesto';
import { TEMI } from './temi';
import { titoloWidget } from './titolo';
import { COMPONENTI_WIDGET, ICONE_WIDGET } from './widget/registro';
import type { ContestoWidget, PropsWidget } from './widget/tipi';

/** Cosa serve per trascinare il widget: lo passa la griglia in modifica. */
export interface Trascinamento {
  attributi: HTMLAttributes<HTMLElement>;
  ascoltatori: Record<string, unknown> | undefined;
}

interface Props {
  widget: WidgetIstanza;
  contesto: ContestoWidget;
  inModifica: boolean;
  dispatch: (azione: AzioneLayout) => void;
  onImpostazioni: (id: string) => void;
  indice: number;
  totale: number;
  refNodo?: (el: HTMLElement | null) => void;
  trascinamento?: Trascinamento;
  /** Il posto lasciato libero dal widget mentre lo si trascina. */
  segnaposto?: boolean;
}

/** Quanto traballa: i widget grandi si muovono meno, se no sembrano ubriachi. */
const AMPIEZZA: Record<Taglia, string> = { S: '1deg', M: '0.55deg', L: '0.45deg', XL: '0.22deg' };

/** Le formine delle taglie, disegnate in proporzione. */
const FORMINA: Record<Taglia, string> = { S: 'h-2.5 w-2.5', M: 'h-2.5 w-5', L: 'h-4 w-5', XL: 'h-4 w-8' };

const haImpostazioni = (widget: WidgetIstanza) => Object.keys(CATALOGO[widget.tipo].impostazioniPredefinite).length > 0;

/** Un clic sui controlli non deve far partire il trascinamento del widget. */
const fermaTrascinamento = {
  onMouseDown: (e: SyntheticEvent) => e.stopPropagation(),
  onTouchStart: (e: SyntheticEvent) => e.stopPropagation(),
  onKeyDown: (e: SyntheticEvent) => e.stopPropagation(),
};

function Contenuto({ widget, contesto, inModifica }: Pick<Props, 'widget' | 'contesto' | 'inModifica'>) {
  const ref = useRef<HTMLDivElement>(null);
  // React 18 non conosce ancora l'attributo inert: si imposta a mano.
  useEffect(() => { if (ref.current) ref.current.inert = inModifica; }, [inModifica]);
  // L'unico punto in cui il tipo si allarga: la mappa garantisce che a ogni
  // tipo corrisponda il componente con le impostazioni giuste.
  const Componente = COMPONENTI_WIDGET[widget.tipo] as ComponentType<PropsWidget>;
  return (
    <div ref={ref} className={cn('relative z-10 mt-2.5 min-h-0 flex-1', inModifica && 'pointer-events-none select-none')}>
      <ErrorBoundary
        resetKey={JSON.stringify([widget.taglia, widget.impostazioni])}
        fallback={(riprova) => (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 text-center text-xs opacity-80">
            Questo widget ha avuto un problema.
            <button type="button" onClick={riprova} className="font-semibold underline underline-offset-2">Riprova</button>
          </div>
        )}
      >
        <Componente id={widget.id} taglia={widget.taglia} impostazioni={widget.impostazioni} contesto={contesto} />
      </ErrorBoundary>
    </div>
  );
}

function ControlliModifica({ widget, dispatch, onImpostazioni, indice, totale }: Omit<Props, 'contesto' | 'inModifica'>) {
  const def = CATALOGO[widget.tipo];
  const sposta = (verso: VersoSpostamento) => dispatch({ tipo: 'sposta', id: widget.id, verso });
  return (
    <>
      <button
        type="button"
        {...fermaTrascinamento}
        onClick={() => dispatch({ tipo: 'rimuovi', id: widget.id })}
        aria-label={`Togli ${def.titolo} dalla dashboard`}
        title="Togli dalla dashboard"
        className="absolute -left-2 -top-2 z-30 flex h-7 w-7 items-center justify-center rounded-full bg-neutral-800 text-white shadow-lg ring-2 ring-white transition hover:scale-110 hover:bg-neutral-700"
      >
        <Minus className="h-4 w-4" strokeWidth={3} />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            {...fermaTrascinamento}
            aria-label={`Opzioni di ${def.titolo}`}
            className="absolute -right-2 -top-2 z-30 flex h-7 w-7 items-center justify-center rounded-full bg-white text-neutral-800 shadow-lg ring-1 ring-black/10 transition hover:scale-110"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {haImpostazioni(widget) && (
            <>
              <DropdownMenuItem onSelect={() => onImpostazioni(widget.id)}>
                <Settings2 className="mr-2 h-4 w-4" /> Impostazioni…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem disabled={indice === 0} onSelect={() => sposta('prima')}>Sposta prima</DropdownMenuItem>
          <DropdownMenuItem disabled={indice === totale - 1} onSelect={() => sposta('dopo')}>Sposta dopo</DropdownMenuItem>
          <DropdownMenuItem disabled={indice === 0} onSelect={() => sposta('inizio')}>Porta in cima</DropdownMenuItem>
          <DropdownMenuItem disabled={indice === totale - 1} onSelect={() => sposta('fine')}>Porta in fondo</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600 focus:text-red-700" onSelect={() => dispatch({ tipo: 'rimuovi', id: widget.id })}>
            <Minus className="mr-2 h-4 w-4" /> Togli dalla dashboard
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {def.taglie.length > 1 && (
        <div
          {...fermaTrascinamento}
          role="radiogroup"
          aria-label={`Taglia di ${def.titolo}`}
          className="absolute bottom-2 left-1/2 z-30 flex -translate-x-1/2 items-center gap-0.5 rounded-full bg-white/95 p-1 shadow-lg ring-1 ring-black/10 backdrop-blur"
        >
          {def.taglie.map((t) => {
            const attiva = t === widget.taglia;
            return (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={attiva}
                aria-label={ETICHETTE_TAGLIA[t]}
                title={ETICHETTE_TAGLIA[t]}
                onClick={() => dispatch({ tipo: 'taglia', id: widget.id, taglia: t })}
                className={cn('flex h-7 min-w-[2rem] items-center justify-center rounded-full px-1.5 transition', attiva ? 'bg-neutral-900 text-white' : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700')}
              >
                <span className={cn('rounded-[3px] border-2 border-current', FORMINA[t])} />
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

function CorniceWidgetBase(props: Props) {
  const { widget, contesto, inModifica, refNodo, trascinamento, segnaposto, indice } = props;
  const def = CATALOGO[widget.tipo];
  const Icona = ICONE_WIDGET[widget.tipo];
  const tema = TEMI[widget.tipo];
  const apri = def.apri && def.apri.ruoli.includes(contesto.ruolo) ? def.apri : null;
  const titolo = titoloWidget(widget);

  if (segnaposto) {
    return <div ref={refNodo} className={cn(CLASSI_TAGLIA[widget.taglia], 'rounded-[1.75rem] border-2 border-dashed border-primary-300 bg-primary-50/60')} />;
  }

  const stileModifica = inModifica
    ? ({ '--ampiezza': AMPIEZZA[widget.taglia], animationDelay: `${-(indice % 5) * 0.07}s` } as CSSProperties)
    : ({ animationDelay: `${indice * 45}ms`, animationFillMode: 'both' } as CSSProperties);

  // Il contenitore esterno e' quello che la libreria del trascinamento misura:
  // deve restare fermo. Traballa (e fa l'entrata) quello interno: con la
  // rotazione sull'esterno le misure sbagliavano e le frecce della tastiera
  // non spostavano piu' il widget.
  return (
    <div
      ref={refNodo}
      {...(inModifica ? trascinamento?.attributi : {})}
      {...(inModifica ? (trascinamento?.ascoltatori as HTMLAttributes<HTMLDivElement>) : {})}
      aria-label={inModifica ? `${titolo}: trascina per spostare` : undefined}
      className={cn(
        CLASSI_TAGLIA[widget.taglia],
        'relative min-h-0 min-w-0 rounded-[1.75rem] outline-none',
        inModifica && 'cursor-grab touch-manipulation focus-visible:ring-4 focus-visible:ring-primary-300 active:cursor-grabbing',
      )}
    >
     <div
      style={stileModifica}
      className={cn(
        'relative h-full',
        inModifica ? 'animate-traballa motion-reduce:animate-none' : 'animate-in fade-in-0 slide-in-from-bottom-3 duration-500 motion-reduce:animate-none',
      )}
     >
      <article
        aria-label={titolo}
        className={cn(
          'relative isolate flex h-full min-h-0 flex-col overflow-hidden rounded-[1.75rem] p-4 ring-1 ring-black/5 transition duration-300',
          'shadow-[0_12px_32px_-20px_rgba(0,0,0,0.55)]',
          tema.sfondo,
          tema.testo,
          !inModifica && 'hover:-translate-y-1 hover:shadow-[0_22px_44px_-22px_rgba(0,0,0,0.6)]',
        )}
      >
        <div aria-hidden className={cn('pointer-events-none absolute -right-14 -top-16 h-44 w-44 rounded-full blur-3xl', tema.alone)} />
        <header className="relative z-10 flex items-center gap-2">
          <span className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl', tema.icona)}>
            <Icona className="h-4 w-4" />
          </span>
          <h3 className="min-w-0 flex-1 truncate font-display text-[0.95rem] font-semibold tracking-tight">{titolo}</h3>
          {!inModifica && apri && (
            <Link
              to={apri.percorso}
              title={`Apri ${apri.etichetta}`}
              aria-label={`Apri ${apri.etichetta}`}
              className={cn(
                'h-7 flex-shrink-0 items-center gap-0.5 rounded-full px-2 text-[11px] font-semibold opacity-70 transition hover:opacity-100',
                // Sul telefono i riquadri piccoli sono stretti: il titolo vince sulla freccia.
                widget.taglia === 'S' ? 'hidden sm:flex' : 'flex',
                tema.scuro ? 'hover:bg-white/15' : 'hover:bg-black/5',
              )}
            >
              {/* Nei riquadri piccoli basta la freccia: il titolo ha la precedenza. */}
              {widget.taglia !== 'S' && apri.etichetta}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </header>
        <ContestoTema.Provider value={{ scuro: tema.scuro }}>
          <Contenuto widget={widget} contesto={contesto} inModifica={inModifica} />
        </ContestoTema.Provider>
      </article>
      {inModifica && <ControlliModifica {...props} />}
     </div>
    </div>
  );
}

export const CorniceWidget = memo(CorniceWidgetBase);
